/**
 * Settings' Sharing tab, first two cards (TMMAPP-0151 §K 29, prototype state 45, P:738–751):
 *
 * - **Virtual account sharing:** a row per virtual account in `/me`, its name, its privacy chip
 *   (`spacePrivacy`, the one rule) and its people — "You, {names} · invited: {names}" from the served
 *   `sharedWithNames` / `invitedNames` (TMMAPI-0186 U3), or "Owner: {name}" on one shared with the
 *   person. A row opens the sharing dialog (the detail is read on open). The row shows no member
 *   limits: a limit lives in the sharing dialog, by share, where it's set (TMMAPP-0155 item 13; the
 *   prototype's Settings shows none). "Owner: {name}" is the served `ownerDisplayName` alone.
 * - **Account permissions:** a row per open account in `GET /v1/me/accounts`, its name,
 *   "Institution ···· last4" and the permission chip from `grantees[]` (`accountPermissionChip`); a
 *   row opens the account's Permissions tab (the account page where that tab isn't the person's).
 *
 * A locked person reads neither list (the locked state keeps Settings for statements and Renew), and
 * the virtual account rows are text, not buttons: tmm-api refuses the detail while locked.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, ButtonBase, Link, Paper, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { accountsApi, institutionLabel, type Account } from '@/api/accounts';
import type { MeVirtualAccount } from '@/api/me';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorState } from '@/components/ErrorState';
import { accountPermissionChip } from '@/features/accounts/permissionChip';
import { SharingDialog } from '@/features/virtualAccount/SharingDialog';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { ToneChip } from '@/shell/PrivacyChip';
import { spacePrivacy } from '@/shell/privacy';
import { resolveTokens } from '@/theme/theme';
import type { SettingsNotify } from './settingsHref';

const MASK = '····';

/** A content card (visual contract v2 §6) with its `h2` in the heading face. */
function SharingCard({
  title,
  children,
  ...rest
}: {
  title: string;
  children: ReactNode;
  'data-settings-card'?: string;
}) {
  return (
    <Paper
      component="section"
      aria-label={title}
      {...rest}
      sx={{ p: { xs: '18px 16px', sm: '20px 22px' }, display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant="h3" component="h2" sx={{ fontSize: 23, lineHeight: 1.2, mb: '6px' }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function VirtualAccountSharingCard({ onNotify }: { onNotify: SettingsNotify }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const [opening, setOpening] = useState<number | null>(null);
  const [detail, setDetail] = useState<VirtualAccountDetail | null>(null);
  const rows = me.virtualAccounts;

  async function open(row: MeVirtualAccount) {
    if (opening !== null) return;
    setOpening(row.id);
    try {
      setDetail(await virtualAccountsApi.get(row.id));
    } catch {
      onNotify(t('settings.errorGeneric'), 'error');
    } finally {
      setOpening(null);
    }
  }

  function people(row: MeVirtualAccount): string {
    if (!row.mine) {
      return t('settings.vaSharing.owner', {
        name: personName(row.ownerDisplayName, t),
      });
    }
    const who =
      row.sharedWithNames.length > 0
        ? t('settings.vaSharing.youAnd', {
            names: formatList(row.sharedWithNames, locale),
          })
        : t('settings.vaSharing.you');
    return row.invitedNames.length > 0
      ? t('settings.vaSharing.invited', {
          people: who,
          names: formatList(row.invitedNames, locale),
        })
      : who;
  }

  if (rows.length === 0) return null;
  return (
    <SharingCard title={t('settings.vaSharing.title')} data-settings-card="virtual-accounts">
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {rows.map((row) => {
          const chip = spacePrivacy(row, t, locale);
          const ids = {
            chip: `va-sharing-${row.id}-chip`,
            people: `va-sharing-${row.id}-people`,
          };
          const content = (
            <>
              <Typography
                component="span"
                sx={{ flex: '1 1 200px', fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {row.name}
              </Typography>
              <Box component="span" id={ids.chip} sx={{ display: 'inline-flex', maxWidth: '100%' }}>
                <ToneChip tone={chip.tone} label={chip.label} />
              </Box>
              <Box
                component="span"
                sx={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '2px' }}
              >
                <Typography
                  component="span"
                  id={ids.people}
                  data-va-sharing-people=""
                  sx={{ fontSize: 13, lineHeight: '19px', color: label }}
                >
                  {people(row)}
                </Typography>
              </Box>
            </>
          );
          const rowSx = {
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '8px 16px',
            py: '12px',
            textAlign: 'start',
          } as const;
          return (
            <Box component="li" key={row.id} sx={{ borderTop: `1px solid ${tokens.line}` }}>
              {me.locked ? (
                // Locked: tmm-api refuses the detail, so the row is text, never a dead button.
                <Box data-va-sharing-row={row.id} sx={rowSx}>
                  {content}
                </Box>
              ) : (
                <ButtonBase
                  data-va-sharing-row={row.id}
                  aria-label={t('virtualAccount.whoSees', { name: row.name })}
                  // The name says what it opens; the chip and the people follow.
                  aria-describedby={`${ids.chip} ${ids.people}`}
                  aria-busy={opening === row.id || undefined}
                  onClick={() => void open(row)}
                  sx={{
                    ...rowSx,
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: tokens.hover },
                    '&.Mui-focusVisible': {
                      outline: `2px solid ${tokens.focusRing}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {content}
                </ButtonBase>
              )}
            </Box>
          );
        })}
      </Box>
      {detail !== null && (
        <SharingDialog
          detail={detail}
          // Closing re-reads /me, whose rows this card lists (TMMAPP-0158 item 5).
          onClose={() => {
            setDetail(null);
            void refetchMe();
          }}
          onChanged={async () => {
            const [next] = await Promise.all([virtualAccountsApi.get(detail.id), refetchMe()]);
            setDetail(next);
          }}
          onNotify={(message) => onNotify(message)}
          onLeft={() => {
            setDetail(null);
            onNotify(t('virtualAccount.left'));
            void refetchMe();
          }}
        />
      )}
    </SharingCard>
  );
}

/** "Institution ···· last4", whatever of it exists. */
function accountMeta(account: Account): string {
  return [
    institutionLabel(account) ?? '',
    account.last4 !== null && account.last4 !== '' ? `${MASK} ${account.last4}` : '',
  ]
    .filter((part) => part.length > 0)
    .join(' ');
}

function AccountPermissionsCard() {
  const { t } = useTranslation();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setAccounts(await accountsApi.list());
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, []);

  useEffect(() => {
    if (me.locked || started.current) return;
    started.current = true;
    void load();
  }, [me.locked, load]);

  if (me.locked) return null;
  if (failure !== null) {
    return (
      <ErrorState
        variant="section"
        error={failure.error}
        what={t('errors.what.sharing')}
        onRetry={() => void load()}
      />
    );
  }
  const open = (accounts ?? []).filter((account) => account.status !== 'CLOSED');
  if (open.length === 0) return null;
  return (
    <SharingCard title={t('settings.accountPerms.title')} data-settings-card="accounts">
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {open.map((account) => {
          const chip = accountPermissionChip(account, t);
          const permissionsTab = account.access === 'OWNER' && account.management !== 'UNMANAGED';
          const meta = accountMeta(account);
          return (
            <Box
              component="li"
              key={account.id}
              data-account-perm-row={account.id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '8px 16px',
                py: '12px',
                borderTop: `1px solid ${tokens.line}`,
              }}
            >
              <Box
                sx={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column' }}
              >
                <Link
                  component={RouterLink}
                  to={`/me/accounts/${account.id}${permissionsTab ? '?tab=permissions' : ''}`}
                  underline="hover"
                  sx={{ fontWeight: 600, color: tokens.text, overflowWrap: 'anywhere' }}
                >
                  {account.name}
                </Link>
                {meta.length > 0 && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}
                  >
                    {meta}
                  </Typography>
                )}
              </Box>
              {chip !== null && <ToneChip tone={chip.tone} label={chip.label} />}
            </Box>
          );
        })}
      </Box>
    </SharingCard>
  );
}

export function SharingOverview({ onNotify }: { onNotify: SettingsNotify }) {
  return (
    <>
      <VirtualAccountSharingCard onNotify={onNotify} />
      <AccountPermissionsCard />
    </>
  );
}
